import { useEffect, useMemo, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import TableToggle from '../payment/TableToggle';
import BeginnerGuideEditModal from './BeginnerGuideEditModal';
import { hasStaticContentBody, mergeStaticContentWithLanguages } from './beginnerGuideConstants';

function BodyPreview({ detail }) {
  if (!hasStaticContentBody(detail)) {
    return <div className="static-content-card__empty">暫未設置</div>;
  }
  return (
    <div
      className="static-content-card__html"
      dangerouslySetInnerHTML={{ __html: detail.body }}
    />
  );
}

export default function BeginnerGuidePage() {
  const [records, setRecords] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [editOpen, setEditOpen] = useState(false);

  const [switchOn, setSwitchOn] = useState(false);
  const [switchLoading, setSwitchLoading] = useState(false);
  const [switchToggling, setSwitchToggling] = useState(false);

  const mergedDetails = useMemo(
    () => mergeStaticContentWithLanguages(languages, records),
    [languages, records]
  );

  async function loadSwitch() {
    setSwitchLoading(true);
    try {
      const data = await adminApi.getShowBeginnerGuideSwitch();
      setSwitchOn(Boolean(data?.showBeginnerGuide));
    } catch {
      setSwitchOn(false);
    } finally {
      setSwitchLoading(false);
    }
  }

  async function loadData() {
    setLoading(true);
    setError('');
    try {
      const [langList, guideList] = await Promise.all([
        adminApi.newsLanguages(),
        adminApi.getBeginnerGuide(),
      ]);
      setLanguages(Array.isArray(langList) ? langList : []);
      setRecords(Array.isArray(guideList) ? guideList : []);
    } catch (err) {
      setError(err.message || '載入失敗');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadSwitch();
    loadData();
  }, []);

  async function handleSwitchChange(checked) {
    setSwitchToggling(true);
    setError('');
    try {
      await adminApi.setShowBeginnerGuideSwitch({ showBeginnerGuide: checked });
      setSwitchOn(checked);
      setMessage(checked ? '首頁新手指南已啟用' : '首頁新手指南已關閉');
    } catch (err) {
      setError(err.message || '開關設定失敗');
    } finally {
      setSwitchToggling(false);
    }
  }

  function handleSaved() {
    setMessage('修改成功');
    setEditOpen(false);
    loadData();
  }

  return (
    <div className="page-card">
      <div className="global-switch-bar">
        <span className="switch-field">首頁新手指南開關</span>
        <TableToggle
          checked={switchOn}
          disabled={switchLoading || switchToggling}
          ariaLabel="首頁新手指南開關"
          onChange={handleSwitchChange}
        />
      </div>

      <div className="section-header">
        <h2>新手指南</h2>
        <div className="section-header__actions">
          <button type="button" className="btn btn-success" onClick={() => setEditOpen(true)}>
            修改
          </button>
        </div>
      </div>

      {message ? <p className="form-message form-message--success">{message}</p> : null}
      {error ? <p className="form-message form-message--error">{error}</p> : null}

      {loading ? (
        <p className="table-empty">載入中…</p>
      ) : (
        <div className="static-content-view">
          <h3 className="static-content-view__section-title">內容</h3>
          <div className="static-content-grid">
            {mergedDetails.map((detail) => (
              <article key={detail.lang} className="static-content-card">
                <header className="static-content-card__header">{detail._label || detail.lang}</header>
                <BodyPreview detail={detail} />
              </article>
            ))}
          </div>
        </div>
      )}

      <BeginnerGuideEditModal
        open={editOpen}
        initialRecords={records}
        languages={languages}
        onClose={() => setEditOpen(false)}
        onSaved={handleSaved}
      />
    </div>
  );
}
